import { Link } from "react-router-dom";
import { Phone, AlertTriangle, ArrowUpRight, ArrowRight, Clock } from "lucide-react";
import { COMPANY } from "../../content/site";

const base = "btn-glow inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap focus-visible:outline-gold";
const variants = {
  gold: "bg-gold text-navy hover:bg-[#ffd873] px-6 py-3.5 text-[15px]",
  outline: "border border-gold/50 text-paper hover:border-gold hover:text-gold px-6 py-3.5 text-[15px] bg-navy/40 backdrop-blur",
  alert: "bg-alert-strong text-white hover:bg-[#b8233a] px-6 py-3.5 text-[15px]",
  ghost: "text-paper hover:text-gold px-1 py-2 text-[15px]",
};

export const Btn = ({ to, href, variant = "gold", children, testid, className = "", external, state }) => {
  const cls = `${base} ${variants[variant]} ${className}`;
  if (to) return <Link to={to} state={state} className={cls} data-testid={testid}>{children}</Link>;
  return (
    <a href={href} className={cls} data-testid={testid} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
      {children}
    </a>
  );
};

export const HotlineBtn = ({ testid, variant = "gold", className = "" }) => (
  <Btn href={COMPANY.hotlineTel} variant={variant} testid={testid} className={className}>
    <Phone className="h-4 w-4" aria-hidden="true" />
    <span>Call <span className="font-mono tracking-tight">{COMPANY.hotline}</span></span>
  </Btn>
);

export const ReportBtn = ({ testid, variant = "outline", className = "" }) => (
  <Btn to="/report" variant={variant} testid={testid} className={className}>
    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
    Report an Issue
  </Btn>
);

export const Eyebrow = ({ children, className = "", tone = "gold" }) => (
  <p className={`font-mono text-[11px] sm:text-xs uppercase tracking-[0.22em] ${tone === "alert" ? "text-alert-soft" : "text-gold"} ${className}`}>
    <span aria-hidden="true" className="mr-2 inline-block h-px w-6 align-middle bg-current opacity-60" />
    {children}
  </p>
);

export const Reveal = ({ as: Tag = "div", delay = 0, className = "", children, ...rest }) => (
  <Tag data-reveal="" style={{ "--d": `${delay}ms` }} className={className} {...rest}>
    {children}
  </Tag>
);

export const SectionHead = ({ eyebrow, title, intro, className = "", align = "left" }) => (
  <Reveal className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} ${className}`}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.04] tracking-[-0.03em] text-paper">{title}</h2>
    {intro && <p className="mt-6 text-base md:text-lg leading-relaxed text-mist">{intro}</p>}
  </Reveal>
);

const trackGlow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

export const GlowCard = ({ as: Tag = "div", className = "", children, delay = 0, ...rest }) => (
  <Tag
    data-reveal=""
    style={{ "--d": `${delay}ms` }}
    onPointerMove={trackGlow}
    className={`card-glow rounded-2xl border hairline bg-deep/70 transition-[border-color,transform] duration-500 hover:border-gold/35 ${className}`}
    {...rest}
  >
    {children}
  </Tag>
);

export const PageHero = ({ eyebrow, title, intro, image, imageAlt = "", testid = "page-hero", children }) => (
  <section data-testid={testid} className="relative overflow-hidden border-b hairline">
    {image && (
      <div className="absolute inset-0" aria-hidden="true">
        <img src={image} srcSet={image.endsWith(".webp") ? `${image.replace(".webp", "-sm.webp")} 960w, ${image} 1600w` : undefined} sizes="100vw" alt={imageAlt} className="h-full w-full object-cover opacity-45" loading="eager" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/40" />
      </div>
    )}
    <div className="gold-glow pointer-events-none absolute -left-40 -top-40 h-[520px] w-[520px]" aria-hidden="true" />
    <div className="container-x relative pb-16 pt-16 sm:pb-24 sm:pt-24 lg:pb-28 lg:pt-32">
      {eyebrow && <Eyebrow className="rise">{eyebrow}</Eyebrow>}
      <h1 className="rise mt-6 max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] tracking-[-0.035em]" style={{ "--d": "80ms" }}>
        {title}
      </h1>
      {intro && (
        <p className="rise mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-mist" style={{ "--d": "160ms" }}>
          {intro}
        </p>
      )}
      {children && <div className="mt-10">{children}</div>}
    </div>
  </section>
);

export const CtaBand = ({ title = "Something broken right now?", copy = "Our emergency desk is open 24 hours a day, 365 days a year. Call now, or report it online.", secondary }) => (
  <section className="relative overflow-hidden" data-testid="cta-band">
    <div className="container-x py-20 sm:py-28">
      <Reveal className="relative overflow-hidden rounded-[28px] border hairline bg-deep px-6 py-14 sm:px-12 sm:py-20">
        <div className="gold-glow pointer-events-none absolute -right-24 -top-32 h-[520px] w-[520px]" aria-hidden="true" />
        <div className="relative grid gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-end">
          <div>
            <Eyebrow tone="alert">Emergency desk · Open 24/7</Eyebrow>
            <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.04]">{title}</h2>
            <p className="mt-5 max-w-xl text-base md:text-lg text-mist">{copy}</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
            <HotlineBtn testid="cta-hotline-link" />
            <ReportBtn testid="cta-report-button" />
            {secondary}
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const FormPlaceholder = ({ title, testid = "form-placeholder" }) => (
  <div data-testid={testid} className="relative overflow-hidden rounded-3xl border border-dashed border-gold/30 bg-deep/60 p-8 sm:p-12">
    <div className="gold-glow pointer-events-none absolute -right-20 -bottom-24 h-80 w-80" aria-hidden="true" />
    <div className="relative">
      <p className="font-mono text-xs uppercase tracking-[0.22em] text-gold flex items-center gap-2">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" /> {title} · Coming soon
      </p>
      <h3 className="mt-5 text-2xl sm:text-3xl font-semibold tracking-tight">Form coming soon.</h3>
      <p className="mt-4 max-w-lg text-mist leading-relaxed">
        In the meantime, call{" "}
        <a href={COMPANY.hotlineTel} className="font-mono text-gold link-underline" data-testid={`${testid}-hotline`}>{COMPANY.hotline}</a>{" "}
        or email{" "}
        <a href={`mailto:${COMPANY.email}`} className="text-gold link-underline" data-testid={`${testid}-email`}>{COMPANY.email}</a>.
      </p>
    </div>
  </div>
);

export const ArrowLink = ({ to, children, testid, className = "" }) => (
  <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-2 text-[15px] font-medium text-gold ${className}`}>
    <span className="link-underline">{children}</span>
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
  </Link>
);

export const ExtIcon = ArrowUpRight;

export const NumberedGrid = ({ items, cols = "lg:grid-cols-4", testid }) => (
  <div data-testid={testid} className={`grid gap-5 sm:grid-cols-2 ${cols}`}>
    {items.map((it, i) => (
      <GlowCard key={it.title} delay={(i % 4) * 70} className="p-7 sm:p-8">
        <p className="font-mono text-xs text-gold">{String(i + 1).padStart(2, "0")}</p>
        <h3 className="mt-6 text-xl font-semibold tracking-tight">{it.title}</h3>
        {it.sub && <p className="mt-1 text-sm text-gold/80">{it.sub}</p>}
        <p className="mt-3 text-[15px] leading-relaxed text-mist">{it.copy}</p>
      </GlowCard>
    ))}
  </div>
);
